/* AMBESA · Buy 3 pay 2 free jersey picker in the cart (markup: snippets/amb-free-gift.liquid).
   One tap on a size adds that jersey, then the theme's own cart update event refreshes the
   drawer, the cart page and the cart bubble. The row of jerseys slides by itself every few
   seconds (paused while the customer hovers, touches, scrolls or types in the box, and off for
   people who ask their device to reduce motion); arrows move it for mouse users.
   In the cart drawer the picker comes into view by itself: when the drawer opens, or the cart
   changes while it is open, and the offer is unlocked, the drawer scrolls to "Free jersey
   unlocked" so the jerseys to pick from are on screen without scrolling. */
import { CartAddEvent } from '@theme/events';

const FALLBACK_ERROR = 'This size could not be added. Please pick another one.';
const AUTO_MS = 4000;
const IDLE_MS = 7000;
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const lastTouch = new WeakMap();

function slide(rail, direction, loop) {
  const card = rail.querySelector('.amb-gift__card');
  if (!card) return;
  const gap = parseFloat(getComputedStyle(rail).columnGap) || 12;
  const step = card.getBoundingClientRect().width + gap;
  const behavior = reduceMotion.matches ? 'auto' : 'smooth';
  const atEnd = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 4;
  if (loop && direction > 0 && atEnd) rail.scrollTo({ left: 0, behavior });
  else rail.scrollBy({ left: direction * step, behavior });
}

['pointerdown', 'wheel', 'touchstart', 'keydown', 'focusin'].forEach((type) => {
  document.addEventListener(
    type,
    (event) => {
      const box = event.target instanceof Element ? event.target.closest('[data-amb-gift]') : null;
      if (box) lastTouch.set(box, Date.now());
    },
    { passive: true, capture: true }
  );
});

setInterval(() => {
  if (reduceMotion.matches || document.hidden) return;
  for (const box of document.querySelectorAll('[data-amb-gift]')) {
    const rail = box.querySelector('.amb-gift__rail');
    if (!rail || box.classList.contains('is-busy')) continue;
    if (!rail.getClientRects().length || rail.scrollWidth <= rail.clientWidth + 4) continue;
    const rect = rail.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > window.innerHeight) continue;
    if (box.matches(':hover') || box.matches(':focus-within')) continue;
    if (Date.now() - (lastTouch.get(box) || 0) < IDLE_MS) continue;
    slide(rail, 1, true);
  }
}, AUTO_MS);

document.addEventListener('click', (event) => {
  const arrow = event.target instanceof Element ? event.target.closest('[data-amb-gift-scroll]') : null;
  if (!arrow) return;
  const rail = arrow.closest('[data-amb-gift]')?.querySelector('.amb-gift__rail');
  if (rail) slide(rail, Number(arrow.dataset.ambGiftScroll), true);
});

document.addEventListener('click', async (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-amb-gift-add]') : null;
  if (!button || button.disabled) return;

  const box = button.closest('[data-amb-gift]');
  if (!box || box.classList.contains('is-busy')) return;
  event.preventDefault();

  const errorEl = box.querySelector('[data-amb-gift-error]');
  if (errorEl) errorEl.hidden = true;
  box.classList.add('is-busy');
  button.setAttribute('aria-busy', 'true');

  const id = Number(button.dataset.ambGiftAdd);
  const sections = [
    ...new Set(
      [...document.querySelectorAll('cart-items-component[data-section-id]')].map((el) => el.dataset.sectionId)
    ),
  ];

  try {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id, quantity: 1 }],
        sections: sections.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message || '');

    document.dispatchEvent(
      new CartAddEvent({}, 'amb-free-gift', {
        source: 'product-form-component',
        itemCount: 1,
        variantId: String(id),
        productId: button.dataset.productId,
        sections: data.sections,
      })
    );
  } catch (error) {
    box.classList.remove('is-busy');
    button.removeAttribute('aria-busy');
    if (errorEl) {
      errorEl.textContent = (error && error.message) || FALLBACK_ERROR;
      errorEl.hidden = false;
    }
  }
});

/* ---------- Cart drawer: bring "Free jersey unlocked" into view ----------
   Runs when the drawer opens and after the drawer content changes. Each unlocked box is shown
   once per opening, so a customer who scrolls back up to the cart lines is left alone. If the
   picker is taller than the part of the drawer that scrolls (short phones), the drawer footer
   stops being sticky so the whole drawer scrolls and the picker fits on screen. */
const DRAWER = '.cart-drawer__dialog';
let shownBox = null;
let shownKey = '';
let timer = 0;

function scrollBox(el, stop) {
  for (let p = el.parentElement; p && p !== stop; p = p.parentElement) {
    const oy = getComputedStyle(p).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && p.scrollHeight > p.clientHeight + 1) return p;
  }
  return null;
}

function bringIntoView(dialog, box) {
  const scroller = scrollBox(box, dialog);
  if (!scroller) return;
  const view = scroller.getBoundingClientRect();
  const rect = box.getBoundingClientRect();
  if (rect.top >= view.top - 1 && rect.bottom <= view.bottom + 1) return;
  const top = rect.top - view.top + scroller.scrollTop;
  const fitBottom = top + rect.height + 12 - scroller.clientHeight;
  const target = Math.max(0, Math.min(fitBottom, top - 8));
  scroller.scrollTo({ top: target, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
}

function fitAndShow(dialog, box) {
  const scroller = scrollBox(box, dialog);
  if (scroller && dialog.getAttribute('cart-summary-sticky') === 'true' && box.offsetHeight + 24 > scroller.clientHeight) {
    dialog.setAttribute('cart-summary-sticky', 'false');
    requestAnimationFrame(() => requestAnimationFrame(() => bringIntoView(dialog, box)));
    return;
  }
  bringIntoView(dialog, box);
}

function revealGift() {
  const dialog = document.querySelector(`${DRAWER}[open]`);
  const box = dialog ? dialog.querySelector('[data-amb-gift]') : null;
  if (!box) {
    shownBox = null;
    shownKey = '';
    return;
  }
  const key = (box.querySelector('.amb-gift__text') || box).textContent.trim();
  if (box === shownBox && key === shownKey) return;
  shownBox = box;
  shownKey = key;
  fitAndShow(dialog, box);
}

function queueReveal() {
  window.clearTimeout(timer);
  timer = window.setTimeout(revealGift, 180);
}

new MutationObserver((records) => {
  for (const record of records) {
    const target = record.target;
    if (record.type === 'attributes') {
      if (!(target instanceof Element) || !target.matches(DRAWER)) continue;
      if (record.attributeName === 'cart-summary-sticky') {
        /* the theme measures its footer again when the drawer opens: keep the picker on screen */
        if (target.getAttribute('cart-summary-sticky') === 'true' && shownBox && target.contains(shownBox)) {
          fitAndShow(target, shownBox);
        }
        continue;
      }
      if (!target.hasAttribute('open')) {
        shownBox = null;
        shownKey = '';
      }
      queueReveal();
      return;
    }
    if (target instanceof Element && target.closest(DRAWER)) {
      queueReveal();
      return;
    }
  }
}).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['open', 'cart-summary-sticky'] });